import API_BASE_URL from './config';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

function StaffProfile() {
  const { staffId } = useParams();
  const [staff, setStaff] = useState(null);
  const [skillGuides, setSkillGuides] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [assignDogId, setAssignDogId] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [assignMessage, setAssignMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedStaff, setEditedStaff] = useState({});
  const [collectingIncome, setCollectingIncome] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const fetchStaff = useCallback(async () => {
    try {
      const playerId = localStorage.getItem('playerId');
      if (!playerId) throw new Error('Please log in');

      const [staffRes, guidesRes, dogsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/staff-profile/${staffId}`, {
          headers: { 'player-id': playerId }
        }),
        fetch(`${API_BASE_URL}/skill-guides`, {
          headers: { 'player-id': playerId }
        }),
        fetch(`${API_BASE_URL}/my-dogs`, {
          headers: { 'player-id': playerId }
        })
      ]);

      let staffData, guidesData, dogsData;

      if (!staffRes.ok) {
        const text = await staffRes.text();
        console.error('Staff response:', text);
        throw new Error(`Failed to fetch staff: ${staffRes.status} ${text}`);
      }
      staffData = await staffRes.json();

      if (!guidesRes.ok) {
        const errorData = await guidesRes.json();
        throw new Error(errorData.error || 'Failed to fetch skill guides');
      }
      guidesData = await guidesRes.json();

      if (!dogsRes.ok) {
        const errorData = await dogsRes.text();
        console.error('Dogs response:', errorData);
        throw new Error(`Failed to fetch dogs: ${dogsRes.status} ${errorData}`);
      }
      dogsData = await dogsRes.json();

      console.log('[FETCH_STAFF] Fetched skills:', staffData.skills?.map(s => ({
        name: s.name,
        level: s.level,
        isProfession: s.isProfession,
        professionBonus: s.professionBonus
      }) || []));
      setStaff(staffData);
      setSkillGuides(guidesData);
      setDogs(dogsData.filter(dog => dog.status === 'Approved'));
      setEditedStaff({
        staffName: staffData.staffName,
        personality: staffData.personality || '',
        biography: staffData.biography || ''
      });
      setMessage('');
      setRetryCount(0);
    } catch (error) {
      console.error('[FETCH_STAFF] Error:', error.message);
      if (retryCount < 3) {
        setTimeout(() => setRetryCount(prev => prev + 1), 5000);
        setMessage(`Retrying... (${retryCount + 1}/3)`);
      } else {
        setMessage(`Error: Failed to load staff profile. ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, [staffId, retryCount]);

  useEffect(() => {
    fetchStaff();
  }, [staffId, fetchStaff]);

  const saveChanges = async () => {
    try {
      const playerId = localStorage.getItem('playerId');
      const payload = {
        staffName: editedStaff.staffName,
        personality: editedStaff.personality,
        biography: editedStaff.biography
      };
      const res = await fetch(`${API_BASE_URL}/staff/${staffId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Update failed');
      }
      const data = await res.json();
      setStaff(data);
      setEditedStaff({
        staffName: data.staffName,
        personality: data.personality || '',
        biography: data.biography || ''
      });
      setIsEditing(false);
      setMessage('Profile updated!');
    } catch (error) {
      setMessage('Error: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedStaff(prev => ({ ...prev, [field]: value }));
  };

  const setProfession = async (skillName, level) => {
    try {
      console.log(`[SET_PROFESSION] Sending: skillName=${skillName}, level=${level}`);
      const res = await fetch(`${API_BASE_URL}/staff/${staffId}/set-profession`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'player-id': localStorage.getItem('playerId')
        },
        body: JSON.stringify({ skillName, level })
      });
      const data = await res.json();
      console.log('[SET_PROFESSION] Response:', {
        status: res.status,
        skills: data.skills ? data.skills.map(s => ({
          name: s.name,
          level: s.level,
          isProfession: s.isProfession,
          professionBonus: s.professionBonus
        })) : 'No skills',
        error: data.error
      });
      if (!res.ok) {
        console.error('[SET_PROFESSION] Failed:', data.error, res.status);
        throw new Error(data.error || 'Failed to set profession');
      }
      await fetchStaff();
      const isProfessionSet = data.skills?.find(s => s.name === skillName && s.level === level)?.isProfession;
      if (!isProfessionSet) {
        console.warn(`[SET_PROFESSION] Warning: ${skillName} (Level ${level}) not set as profession in response`);
        setMessage(`Error: Failed to set ${skillName} as profession`);
        return;
      }
      setMessage(`Set ${skillName} as profession!`);
    } catch (error) {
      console.error('[SET_PROFESSION] Error:', error.message);
      setMessage('Error setting profession: ' + error.message);
    }
  };

  const toggleHobby = async (skillName, level, action) => {
    try {
      const res = await fetch(`${API_BASE_URL}/staff/${staffId}/hobby`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'player-id': localStorage.getItem('playerId')
        },
        body: JSON.stringify({ skillName, level, action })
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update hobby');
      }
      setStaff(data);
      setMessage(`${action === 'add' ? 'Added' : 'Removed'} ${skillName} as hobby`);
    } catch (error) {
      setMessage('Error updating hobby: ' + error.message);
    }
  };

  const collectIncome = async () => {
    try {
      setCollectingIncome(true);
      const res = await fetch(`${API_BASE_URL}/staff/${staffId}/collect-income`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': localStorage.getItem('playerId')
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to collect income');
      }
      await fetchStaff();
      setMessage(`Collected ${data.totalIncome} DFG$ for ${staff?.staffName || 'staff'}!`);
    } catch (error) {
      setMessage('Error collecting income: ' + error.message);
    } finally {
      setCollectingIncome(false);
    }
  };

  const handleAssignDog = async (e) => {
    e.preventDefault();
    setAssignMessage('');
    if (!assignDogId) {
      setAssignMessage('Error: Please select a dog');
      return;
    }
    try {
      const playerId = localStorage.getItem('playerId');
      const response = await fetch(`${API_BASE_URL}/assign-dog-to-staff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'player-id': playerId
        },
        body: JSON.stringify({
          playerId,
          dogId: assignDogId,
          staffId
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Assignment failed');
      }
      const data = await response.json();
      setAssignMessage(data.message);
      await fetchStaff();
      setAssignDogId('');
    } catch (error) {
      setAssignMessage('Error: ' + error.message);
    }
  };

  const getHighestLevelSkills = (skills) => {
    const skillMap = {};
    (skills || []).forEach(skill => {
      if (!skillMap[skill.name] || skillMap[skill.name].level < skill.level) {
        skillMap[skill.name] = skill;
      }
    });
    return Object.values(skillMap);
  };

  if (loading) return <p style={{ textAlign: 'center', color: '#888', paddingTop: '60px' }}>Loading...</p>;
  if (message && !staff) return (
    <div style={{ textAlign: 'center', paddingTop: '60px' }}>
      <p style={{ color: '#EF5350', fontSize: '16px', marginBottom: '20px' }}>{message}</p>
      <button
        onClick={() => { setRetryCount(0); fetchStaff(); }}
        style={{
          padding: '10px 20px',
          backgroundColor: '#1E88E5',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        Retry
      </button>
    </div>
  );
  if (!staff) return <p style={{ textAlign: 'center', color: '#888', paddingTop: '60px' }}>Staff not found.</p>;

  const highestLevelSkills = getHighestLevelSkills(staff.skills);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', paddingTop: '60px' }}>
      {message && (
        <div style={{
          backgroundColor: message.includes('Error') ? '#EF5350' : '#4CAF50',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {message}
        </div>
      )}
      <h1 style={{ color: '#1E88E5', fontSize: '1.8em', margin: '0 0 10px' }}>
        {isEditing ? (
          <input
            value={editedStaff.staffName}
            onChange={(e) => handleInputChange('staffName', e.target.value)}
            style={{ fontSize: '1.8em', padding: '5px', borderRadius: '5px', border: '1px solid #ccc', width: '100%', maxWidth: '400px' }}
          />
        ) : (
          staff.staffName
        )}
      </h1>
      {staff.imageLink ? (
        <img
          src={staff.imageLink}
          alt={staff.staffName}
          style={{ width: '300px', height: '300px', objectFit: 'contain', marginBottom: '20px', borderRadius: '8px' }}
          onError={() => setMessage('Error loading image')}
        />
      ) : (
        <div style={{ width: '300px', height: '300px', backgroundColor: '#ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px', borderRadius: '8px' }}>
          No Image
        </div>
      )}
      <div style={{ marginBottom: '20px' }}>
        {isEditing ? (
          <>
            <button
              onClick={saveChanges}
              style={{ padding: '5px 10px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '10px', fontWeight: 'bold' }}
              onMouseOver={e => e.target.style.backgroundColor = '#45a049'}
              onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
            >
              Save
            </button>
            <button
              onClick={() => setIsEditing(false)}
              style={{ padding: '5px 10px', backgroundColor: '#EF5350', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
              onMouseOver={e => e.target.style.backgroundColor = '#e53935'}
              onMouseOut={e => e.target.style.backgroundColor = '#EF5350'}
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsEditing(true)}
            style={{ padding: '5px 10px', backgroundColor: '#1E88E5', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold' }}
            onMouseOver={e => e.target.style.backgroundColor = '#1565C0'}
            onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
          >
            Edit Profile
          </button>
        )}
      </div>
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={collectIncome}
          disabled={collectingIncome}
          style={{
            padding: '10px 20px',
            backgroundColor: collectingIncome ? '#ccc' : '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: collectingIncome ? 'not-allowed' : 'pointer',
            fontWeight: 'bold'
          }}
          onMouseOver={e => !collectingIncome && (e.target.style.backgroundColor = '#F57C00')}
          onMouseOut={e => !collectingIncome && (e.target.style.backgroundColor = '#FF9800')}
        >
          {collectingIncome ? 'Collecting...' : 'Collect Daily Income'}
        </button>
      </div>
      <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', textAlign: 'left' }}>
        <div style={{ flex: 1, backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Personality</h2>
          {isEditing ? (
            <textarea
              value={editedStaff.personality}
              onChange={(e) => handleInputChange('personality', e.target.value)}
              placeholder="Describe their personality..."
              style={{ width: '100%', height: '150px', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '16px' }}
            />
          ) : (
            <p style={{ color: '#333', fontSize: '16px' }}>{staff.personality || 'No personality description.'}</p>
          )}
        </div>
        <div style={{ flex: 1, backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Biography</h2>
          {isEditing ? (
            <textarea
              value={editedStaff.biography}
              onChange={(e) => handleInputChange('biography', e.target.value)}
              placeholder="Tell their story..."
              style={{ width: '100%', height: '150px', padding: '10px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '16px' }}
            />
          ) : (
            <p style={{ color: '#333', fontSize: '16px' }}>{staff.biography || 'No biography.'}</p>
          )}
        </div>
      </div>
      <div style={{ backgroundColor: '##f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', marginBottom: '20px', textAlign: 'left' }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Skills & Roles</h2>
        {highestLevelSkills.length > 0 ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px', color: '#333' }}>
            <thead>
              <tr style={{ backgroundColor: '#1E88E5', color: 'white' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Skill</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Level</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Title</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Salary</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Bonus</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {highestLevelSkills.map((skill, idx) => {
                const guide = skillGuides.find(g => g.name === skill.name);
                const levelData = guide?.levels.find(l => l.level === skill.level) || {};
                const salary = levelData.salary ? `${levelData.salary} DFG$` : 'N/A';
                const bonus = levelData.bonus || 'Coming Soon';
                return (
                  <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>{skill.name}</td>
                    <td style={{ padding: '10px' }}>{skill.level}</td>
                    <td style={{ padding: '10px' }}>{levelData.title || skill.title || 'Unknown'}</td>
                    <td style={{ padding: '10px', color: skill.isProfession ? '#4CAF50' : '#888' }}>{salary}</td>
                    <td style={{ padding: '10px', color: '#888' }}>{bonus}</td>
                    <td style={{ padding: '10px' }}>
                      {skill.isProfession && (
                        <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>Profession ⭐</span>
                      )}
                      {skill.isHobby && (
                        <span style={{ color: '#FF9800', fontWeight: 'bold', marginLeft: skill.isProfession ? '10px' : '0' }}>Hobby 🎨</span>
                      )}
                      {!skill.isProfession && !skill.isHobby && 'None'}
                    </td>
                    <td style={{ padding: '10px' }}>
                      <button
                        onClick={() => setProfession(skill.name, skill.level)}
                        disabled={skill.isProfession}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: skill.isProfession ? '#ccc' : '#1E88E5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: skill.isProfession ? 'not-allowed' : 'pointer',
                          fontSize: '12px',
                          marginRight: '5px'
                        }}
                        onMouseOver={e => !skill.isProfession && (e.target.style.backgroundColor = '#1565C0')}
                        onMouseOut={e => !skill.isProfession && (e.target.style.backgroundColor = '#1E88E5')}
                      >
                        {skill.isProfession ? 'Current Profession' : 'Set Profession'}
                      </button>
                      <button
                        onClick={() => toggleHobby(skill.name, skill.level, skill.isHobby ? 'remove' : 'add')}
                        disabled={staff.skills.filter(s => s.isHobby).length >= 2 && !skill.isHobby}
                        style={{
                          padding: '5px 10px',
                          backgroundColor: skill.isHobby ? '#FF9800' : '#1E88E5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: staff.skills.filter(s => s.isHobby).length >= 2 && !skill.isHobby ? 'not-allowed' : 'pointer',
                          fontSize: '12px'
                        }}
                        onMouseOver={e => !(staff.skills.filter(s => s.isHobby).length >= 2 && !skill.isHobby) && (e.target.style.backgroundColor = skill.isHobby ? '#F57C00' : '#1565C0')}
                        onMouseOut={e => !(staff.skills.filter(s => s.isHobby).length >= 2 && !skill.isHobby) && (e.target.style.backgroundColor = skill.isHobby ? '#FF9800' : '#1E88E5')}
                      >
                        {skill.isHobby ? 'Remove Hobby' : 'Add Hobby'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ color: '#333', fontSize: '16px' }}>No skills earned.</p>
        )}
      </div>
      <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', marginBottom: '20px', textAlign: 'left' }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Bonuses</h2>
        {(staff.bonuses || []).length > 0 ? (
          <ul style={{ listStyle: 'none', padding: 0, color: '#333', fontSize: '16px' }}>
            {staff.bonuses.map((bonus, idx) => (
              <li key={idx} style={{ marginBottom: '5px' }}>{bonus.type}</li>
            ))}
          </ul>
        ) : (
          <p style={{ color: '#333', fontSize: '16px' }}>No bonuses available.</p>
        )}
      </div>
      <div style={{ backgroundColor: '#f9f9f9', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', marginBottom: '20px', textAlign: 'left' }}>
        <h2 style={{ color: '#4CAF50', marginBottom: '10px' }}>Reputation</h2>
        <div style={{ display: 'flex', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: '#1E88E5', fontSize: '16px', marginBottom: '10px' }}>Dogs</h3>
            {assignMessage && (
              <div style={{
                backgroundColor: assignMessage.includes('Error') ? '#EF5350' : '#4CAF50',
                padding: '10px',
                marginBottom: '10px',
                borderRadius: '5px',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {assignMessage}
              </div>
            )}
            <form onSubmit={handleAssignDog}>
              <div style={{ marginBottom: '10px' }}>
                <label htmlFor="assignDog" style={{ display: 'block', marginBottom: '5px', color: '#333' }}>Assign Dog:</label>
                <select
                  id="assignDog"
                  value={assignDogId}
                  onChange={(e) => setAssignDogId(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #ccc', fontSize: '14px' }}
                >
                  <option value="">-- Select a Dog --</option>
                  {dogs.map(dog => (
                    <option key={dog._id} value={dog._id}>
                      {dog.registeredName} ({dog.breed})
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="submit"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#1E88E5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
                onMouseOver={e => e.target.style.backgroundColor = '#1565C0'}
                onMouseOut={e => e.target.style.backgroundColor = '#1E88E5'}
              >
                Assign Dog
              </button>
            </form>
            <h4 style={{ color: '#1E88E5', fontSize: '16px', margin: '10px 0' }}>Assigned Dogs</h4>
            {(staff.dogReputation || []).length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, color: '#333', fontSize: '16px' }}>
                {staff.dogReputation.map((rep, idx) => (
                  <li key={idx} style={{ marginBottom: '5px' }}>
                    {rep.dogId?.registeredName || 'Unknown Dog'} (Reputation: {rep.reputation})
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#333', fontSize: '16px' }}>No dogs assigned.</p>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ color: '#1E88E5', fontSize: '16px', marginBottom: '10px' }}>Kennels</h3>
            {(staff.kennels || []).length > 0 ? (
              <ul style={{ listStyle: 'none', padding: 0, color: '#333', fontSize: '16px' }}>
                {staff.kennels.map((kennel, index) => (
                  <li key={kennel._id || kennel || index} style={{ marginBottom: '5px' }}>
                    {typeof kennel === 'object' && kennel.kennelName
                      ? kennel.kennelName
                      : `Unnamed Kennel (ID: ${typeof kennel === 'string' ? kennel : kennel._id || 'undefined'})`}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#333', fontSize: '16px' }}>No kennels affiliated.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StaffProfile;